import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EyeOff, RotateCcw, ChevronDown, ChevronUp, ExternalLink, RefreshCw } from "lucide-react";
import { BACKEND_URL } from "../../lib/backend";

const API = `${BACKEND_URL}/api`;
const REASON_LABEL = {
  quality_gate_classifier: "not research (AI)",
  quality_gate_index_page: "index page",
  quality_gate_marketing: "marketing",
  quality_gate_junk: "junk / bot wall",
  duplicate_url: "duplicate",
};

export const HiddenReportsList = ({ dm, onRestored }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/reports/hidden`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hidden reports unavailable");
    }
  }, []);
  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  const restore = async (r) => {
    setBusy(r.id);
    try {
      await axios.post(`${API}/admin/reports/${r.id}/restore`);
      toast.success(`Restored «${(r.title || "").slice(0, 60)}» — pinned, the gates won't hide it again`);
      await load();
      onRestored && onRestored();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(null);
    }
  };

  const rows = (data?.reports || []).filter((r) => filter === "all" || r.hidden_reason === filter);
  const muted = dm ? "text-slate-500" : "text-[#6b7a7d]";
  const btn = dm ? "text-slate-300 hover:text-cyan-300 hover:bg-white/5" : "text-[#3d4a4d] hover:text-[#006874] hover:bg-[#f3f4f5]";

  return (
    <div className={`mt-4 pt-4 border-t ${dm ? "border-white/5" : "border-[#e6eaeb]"}`} data-testid="rs-hidden-section">
      <button onClick={() => setOpen((o) => !o)} className={`flex items-center gap-2 text-xs font-medium ${btn} rounded-md px-1.5 py-1`}
              data-testid="rs-hidden-toggle">
        <EyeOff className="w-3.5 h-3.5" /> Review reports hidden by the quality gates
        {data ? <span className={muted}>({data.total})</span> : null}
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {["all", ...Object.keys(data?.by_reason || {})].map((k) => (
              <button key={k} onClick={() => setFilter(k)}
                      className={`category-tag px-2 py-1 rounded-md ${filter === k ? (dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]") : btn}`}
                      data-testid={`rs-hidden-filter-${k}`}>
                {k === "all" ? "all" : REASON_LABEL[k] || k}{k !== "all" ? ` ${data.by_reason[k]}` : ""}
              </button>
            ))}
            <button onClick={load} className={`ml-auto p-1 rounded-md ${btn}`} aria-label="Reload" data-testid="rs-hidden-reload">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          {!data && <p className={`text-xs ${muted}`}>Loading…</p>}
          {data && rows.length === 0 && <p className={`text-xs ${muted}`} data-testid="rs-hidden-empty">Nothing hidden.</p>}
          <ul className="max-h-80 overflow-y-auto divide-y divide-black/5 dark:divide-white/5" data-testid="rs-hidden-list">
            {rows.map((r) => (
              <li key={r.id} className="py-2 flex items-start gap-3" data-testid={`rs-hidden-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`} title={r.title}>{r.title || r.url}</p>
                  <p className={`text-[11px] truncate ${muted}`}>
                    <span className={`inline-block mr-2 px-1.5 rounded ${dm ? "bg-white/5" : "bg-[#f3f4f5]"}`}>{REASON_LABEL[r.hidden_reason] || r.hidden_reason}</span>
                    {r.organisation || "—"} ·{" "}
                    <a href={r.url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-0.5">
                      {(() => { try { return new URL(r.url).hostname; } catch { return r.url; } })()} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </p>
                </div>
                <button disabled={busy === r.id} onClick={() => restore(r)}
                        className={`flex items-center gap-1 category-tag px-2 py-1 rounded-md disabled:opacity-40 flex-shrink-0 ${btn}`}
                        data-testid={`rs-hidden-restore-${r.id}`}>
                  {busy === r.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HiddenReportsList;
